//deps
import { useCallback, useEffect, useState } from "react";

//internal-deps
import { getBridgeSdk } from "@swiggy-private/checkout-core";
import { LinkedWalletDetails } from "@swiggy-private/payment-ui";
import { useMountedRef } from "@swiggy-private/core-ui";
import { WALLET_CODES } from "@swiggy-private/payment-types";
import { Tenant } from "@swiggy-private/native-bridge/dist/interfaces/Tenant";
import { isEmpty } from "@swiggy-private/js-utils";

export interface GetLinkedWalletDetails {
    isLoading: boolean;
    retry: VoidFunction;
    walletDetails?: LinkedWalletDetails;
}

export const useGetLinkedWalletDetails = (walletCode: WALLET_CODES, tenant?: string): GetLinkedWalletDetails => {
    const [isLoading, setIsLoading] = useState(true);
    const isMountedRef = useMountedRef();

    const [walletDetails, setWalletDetails] = useState<LinkedWalletDetails | undefined>(undefined);

    useEffect(() => {
        if (!isLoading) {
            return;
        }

        getBridgeSdk()
            .getLinkedWalletDetails({
                walletCode,
                ...(!isEmpty(tenant) && { tenant: tenant as Tenant }),
            })
            .then((linkedWalletDetails) => {
                if (isMountedRef.current) {
                    if (linkedWalletDetails) {
                        setWalletDetails({ ...linkedWalletDetails, walletCode });
                    }
                    setIsLoading(false);
                }
            })
            .catch(() => {
                if (isMountedRef.current) {
                    setIsLoading(false);
                }
            });
    }, [isLoading, isMountedRef, tenant, walletCode]);

    const getLinkedWalletDetails = useCallback(() => {
        if (isMountedRef.current) {
            setIsLoading(true);
        }
    }, [isMountedRef]);

    return {
        isLoading,
        retry: getLinkedWalletDetails,
        walletDetails,
    };
};
